import { useEffect, useRef, useState, useMemo } from "react";
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { Tag } from 'primereact/tag';
import { Toast } from 'primereact/toast';
import { InputText } from 'primereact/inputtext';
import { Dropdown } from 'primereact/dropdown';
import { Calendar } from 'primereact/calendar';
import { Dialog } from 'primereact/dialog';
import { InputNumber } from 'primereact/inputnumber';
import { TabView, TabPanel } from 'primereact/tabview';
import { ConfirmDialog, confirmDialog } from 'primereact/confirmdialog';
import { useSearchParams } from 'react-router-dom';
import ChequeService from '../../services/ChequeService';
import FinancialAccountService from '../../services/FinancialAccountService';
import SupplierService from '../../services/SupplierService';
import { customerService } from '../../services/CustomerService';

export default function ChequeListPage() {
    const [searchParams, setSearchParams] = useSearchParams();
    const [highlightedChequeId] = useState(() => Number(searchParams.get('highlight')));
    const initialTabParam = searchParams.get('tab');
    const highlightHandled = useRef(false);

    const [mounted, setMounted] = useState(false);
    useEffect(() => { setMounted(true); }, []);

    const [cheques, setCheques] = useState([]);
    const [financialAccounts, setFinancialAccounts] = useState([]);
    const [suppliers, setSuppliers] = useState([]);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [actionLoading, setActionLoading] = useState(false);

    const [activeTabIndex, setActiveTabIndex] = useState(() => {
        if (initialTabParam === 'customer') return 1;
        if (initialTabParam === 'all') return 2;
        return 0; // Default to 'Kendi Çeklerimiz'
    });

    const [globalFilter, setGlobalFilter] = useState('');
    const [period, setPeriod] = useState('all');
    const [statusFilter, setStatusFilter] = useState(null);
    const [startDate, setStartDate] = useState(null);
    const [endDate, setEndDate] = useState(null);

    const toast = useRef(null);

    // ==========================================
    // MODAL STATES
    // ==========================================
    // 1. Yeni Çek Tanımla Dialog (Kendi Çekimiz)
    const [showCreateDialog, setShowCreateDialog] = useState(false);
    const [newCheque, setNewCheque] = useState({
        amount: 0,
        dueDate: null,
        financialAccountId: null,
        bankName: '',
        bankBranch: '',
        chequeNumber: '',
        drawer: 'Kendi Çekimiz',
        description: '',
        type: 2 // Outbound
    });

    // 2. Ödeme Yap (Kendi Çekimiz - Bankadan Düş) Dialog
    const [payDialogVisible, setPayDialogVisible] = useState(false);
    const [selectedChequeToPay, setSelectedChequeToPay] = useState(null);
    const [payForm, setPayForm] = useState({
        financialAccountId: null,
        transactionDate: new Date(),
        description: ''
    });

    // 3. Tahsil Et (Müşteri Çeki - Kasaya/Bankaya Al) Dialog
    const [collectDialogVisible, setCollectDialogVisible] = useState(false);
    const [selectedChequeToCollect, setSelectedChequeToCollect] = useState(null);
    const [collectForm, setCollectForm] = useState({
        financialAccountId: null,
        transactionDate: new Date(),
        description: ''
    });

    // 4. Tedarikçiye Ciro Et Dialog
    const [endorseDialogVisible, setEndorseDialogVisible] = useState(false);
    const [selectedChequeToEndorse, setSelectedChequeToEndorse] = useState(null);
    const [endorseForm, setEndorseForm] = useState({
        supplierId: null,
        endorsementDate: new Date(),
        description: ''
    });

    // 6. Banka Hesabı Bağlantısı Eksik Çekler Modal State
    const [unlinkedModalVisible, setUnlinkedModalVisible] = useState(false);
    const [unlinkedChequesList, setUnlinkedChequesList] = useState([]);
    const [savingUnlinked, setSavingUnlinked] = useState(false);
    const unlinkedPromptShown = useRef(false);

    // 5. Çek Düzenle Dialog (Portföydeki Çekler)
    const [editDialogVisible, setEditDialogVisible] = useState(false);
    const [selectedChequeToEdit, setSelectedChequeToEdit] = useState(null);
    const [editForm, setEditForm] = useState({
        id: 0,
        customerId: null,
        amount: 0,
        dueDate: null,
        bankName: '',
        bankBranch: '',
        chequeNumber: '',
        drawer: '',
        description: ''
    });

    // Status options for filters
    const statusOptions = [
        { label: 'Tüm Durumlar', value: null },
        { label: 'Portföyde / Kasada', value: 1 },
        { label: 'Verildi / Ciro Edildi', value: 2 },
        { label: 'Tahsil Edildi (Müşteri)', value: 3 },
        { label: 'Ödendi (Firma Çeki)', value: 4 },
        { label: 'İade Edildi', value: 5 }
    ];

    // Load initial accounts and suppliers
    const loadLookups = async () => {
        try {
            const [accRes, supRes, cusRes] = await Promise.all([
                FinancialAccountService.getAll(),
                SupplierService.getAllSuppliers(),
                customerService.list()
            ]);
            const accounts = Array.isArray(accRes)
                ? accRes
                : Array.isArray(accRes?.data)
                ? accRes.data
                : accRes?.data?.data || [];
            setFinancialAccounts(accounts);

            const sups = Array.isArray(supRes)
                ? supRes
                : Array.isArray(supRes?.data)
                ? supRes.data
                : supRes?.data?.data || [];
            setSuppliers(sups);

            const cusList = Array.isArray(cusRes)
                ? cusRes
                : Array.isArray(cusRes?.data)
                ? cusRes.data
                : cusRes?.data?.data || [];
            setCustomers(cusList);
        } catch (err) {
            console.error("Hesap, tedarikçi veya müşteri verileri yüklenemedi", err);
        }
    };

    const accountOptions = useMemo(() => {
        return financialAccounts
            .filter((a) => a.accountType === 2 || a.accountType === 1) // Banka veya Kasa
            .map((a) => ({
                label: `${a.name} (${a.accountType === 2 ? 'Banka' : 'Kasa'}) - ₺${(Number(a.balance) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`,
                value: a.id,
                account: a
            }));
    }, [financialAccounts]);

    const supplierOptions = useMemo(() => {
        return suppliers.map((s) => ({
            label: s.name,
            value: s.id
        }));
    }, [suppliers]);

    const customerOptions = useMemo(() => {
        return customers.map((c) => ({
            label: c.name || c.title || `${c.firstName || ''} ${c.lastName || ''}`.trim() || `Müşteri #${c.id}`,
            value: c.id
        }));
    }, [customers]);

    const unlinkedCompanyCheques = useMemo(() => {
        return cheques.filter((c) => c.type === 2 && (c.status === 1 || c.status === 2) && !c.financialAccountId);
    }, [cheques]);

    const fetchCheques = async () => {
        try {
            setLoading(true);
            const res = await ChequeService.getCheques(null, null);
            setCheques(res.data || []);
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: 'Çekler yüklenirken hata oluştu' });
        } finally {
            setLoading(false);
        }
    };

    // Banka hesabı eksik olan firma çekleri varsa popup aç
    useEffect(() => {
        if (!loading && unlinkedCompanyCheques.length > 0 && !unlinkedPromptShown.current) {
            unlinkedPromptShown.current = true;
            setUnlinkedChequesList(
                unlinkedCompanyCheques.map((c) => ({
                    ...c,
                    targetAccountId: null
                }))
            );
            setUnlinkedModalVisible(true);
        }
    }, [loading, unlinkedCompanyCheques]);

    const clearFilters = () => {
        setGlobalFilter('');
        setStatusFilter(null);
        setPeriod('all');
        setStartDate(null);
        setEndDate(null);
    };

    useEffect(() => {
        fetchCheques();
        loadLookups();
    }, []);

    // Highlight router param handling
    useEffect(() => {
        if (highlightHandled.current || loading || !Number.isFinite(highlightedChequeId) || highlightedChequeId <= 0) return;
        const highlighted = cheques.find((item) => Number(item.id) === highlightedChequeId);
        if (highlighted) {
            setActiveTabIndex(highlighted.type === 2 ? 0 : 1);
        }
        highlightHandled.current = true;
        const nextParams = new URLSearchParams(searchParams);
        nextParams.delete('highlight');
        setSearchParams(nextParams, { replace: true });
    }, [cheques, highlightedChequeId, loading, setSearchParams, searchParams]);

    // ==========================================
    // PERIOD BUTTONS & COUNTS
    // ==========================================
    const activeTabRawRows = useMemo(() => {
        if (activeTabIndex === 0) return cheques.filter(x => x.type === 2);
        if (activeTabIndex === 1) return cheques.filter(x => x.type === 1);
        return cheques;
    }, [cheques, activeTabIndex]);

    const periodCounts = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const day = today.getDay();
        const diffToMonday = (day === 0 ? -6 : 1) - day;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() + diffToMonday);
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

        let allCount = 0;
        let overdueCount = 0;
        let todayCount = 0;
        let weekCount = 0;
        let monthCount = 0;

        activeTabRawRows.forEach(row => {
            if (statusFilter !== null && Number(row.status) !== Number(statusFilter)) {
                return;
            }

            allCount++;
            const isClosed = row.status === 3 || row.status === 4 || row.status === 5;
            const due = row.dueDate ? new Date(row.dueDate) : null;
            if (!due) return;

            const dueDay = new Date(due);
            dueDay.setHours(0, 0, 0, 0);

            if (!isClosed && dueDay < today) {
                overdueCount++;
            }
            if (dueDay.getTime() === today.getTime()) {
                todayCount++;
            }
            if (due >= startOfWeek && due <= endOfWeek) {
                weekCount++;
            }
            if (due >= startOfMonth && due <= endOfMonth) {
                monthCount++;
            }
        });

        return { allCount, overdueCount, todayCount, weekCount, monthCount };
    }, [activeTabRawRows, statusFilter]);

    const periodButtons = [
        { label: "Tümü", value: "all", count: periodCounts.allCount, icon: "pi-list" },
        { label: "Vadesi Geçmiş", value: "overdue", count: periodCounts.overdueCount, icon: "pi-exclamation-triangle" },
        { label: "Bugün", value: "today", count: periodCounts.todayCount, icon: "pi-clock" },
        { label: "Bu Hafta", value: "week", count: periodCounts.weekCount, icon: "pi-calendar-times" },
        { label: "Bu Ay", value: "month", count: periodCounts.monthCount, icon: "pi-calendar" },
        { label: "Özel Tarih", value: "custom", icon: "pi-sliders-h" },
    ];

    // ==========================================
    // FILTERED LISTS
    // ==========================================
    const filterList = (rows) => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        const day = today.getDay();
        const diffToMonday = (day === 0 ? -6 : 1) - day;
        const startOfWeek = new Date(today);
        startOfWeek.setDate(today.getDate() + diffToMonday);
        startOfWeek.setHours(0, 0, 0, 0);

        const endOfWeek = new Date(startOfWeek);
        endOfWeek.setDate(startOfWeek.getDate() + 6);
        endOfWeek.setHours(23, 59, 59, 999);

        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1, 0, 0, 0, 0);
        const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0, 23, 59, 59, 999);

        return rows.filter((row) => {
            // Status filter
            if (statusFilter !== null && Number(row.status) !== Number(statusFilter)) {
                return false;
            }

            const due = row.dueDate ? new Date(row.dueDate) : null;
            if (!due) return true;

            const isClosed = row.status === 3 || row.status === 4 || row.status === 5;
            const dueDay = new Date(due);
            dueDay.setHours(0, 0, 0, 0);

            // Period filter
            if (period === "overdue") {
                if (isClosed) return false;
                if (dueDay >= today) return false;
            } else if (period === "today") {
                if (dueDay.getTime() !== today.getTime()) return false;
            } else if (period === "week") {
                if (due < startOfWeek || due > endOfWeek) return false;
            } else if (period === "month") {
                if (due < startOfMonth || due > endOfMonth) return false;
            } else if (period === "custom") {
                if (startDate) {
                    const start = new Date(startDate);
                    start.setHours(0, 0, 0, 0);
                    if (due < start) return false;
                }
                if (endDate) {
                    const end = new Date(endDate);
                    end.setHours(23, 59, 59, 999);
                    if (due > end) return false;
                }
            }

            return true;
        }).sort((left, right) => {
            if (Number(left.id) === highlightedChequeId) return -1;
            if (Number(right.id) === highlightedChequeId) return 1;

            // Kapanmış çekleri (Ödendi: 4, Tahsil Edildi: 3, İade: 5) listenin altına al
            const isLeftClosed = left.status === 3 || left.status === 4 || left.status === 5;
            const isRightClosed = right.status === 3 || right.status === 4 || right.status === 5;

            if (isLeftClosed && !isRightClosed) return 1;
            if (!isLeftClosed && isRightClosed) return -1;

            // Açık çekler arasında vadesi en acil olandan (en eski geçmiş tarihten ve bugünden) ileriye doğru sırala
            return new Date(left.dueDate) - new Date(right.dueDate);
        });
    };

    const outboundCheques = useMemo(() => filterList(cheques.filter(x => x.type === 2)), [cheques, statusFilter, period, startDate, endDate, highlightedChequeId]);
    const inboundCheques = useMemo(() => filterList(cheques.filter(x => x.type === 1)), [cheques, statusFilter, period, startDate, endDate, highlightedChequeId]);
    const allCheques = useMemo(() => filterList(cheques), [cheques, statusFilter, period, startDate, endDate, highlightedChequeId]);

    // ==========================================
    // DYNAMIC METRICS (KPI)
    // ==========================================
    const metrics = useMemo(() => {
        const today = new Date();
        today.setHours(0, 0, 0, 0);

        if (activeTabIndex === 0) {
            // Kendi Çeklerimiz Metrics
            let pendingCount = 0, pendingAmount = 0;
            let overdueCount = 0, overdueAmount = 0;
            let paidCount = 0, paidAmount = 0;
            let totalCount = outboundCheques.length, totalAmount = 0;

            outboundCheques.forEach((c) => {
                const amount = Number(c.amount) || 0;
                totalAmount += amount;

                if (c.status === 2 || c.status === 1) {
                    pendingCount++;
                    pendingAmount += amount;
                    if (c.dueDate && new Date(c.dueDate) < today) {
                        overdueCount++;
                        overdueAmount += amount;
                    }
                } else if (c.status === 4) {
                    paidCount++;
                    paidAmount += amount;
                }
            });

            return {
                card1: { title: 'ÖDEME BEKLEYEN ÇEKLER', amount: pendingAmount, sub: `${pendingCount} Adet Verilmiş / Portföy`, icon: 'pi-clock', theme: 'amber' },
                card2: { title: 'VADESİ GEÇEN / İBRAZ BEKLEYEN', amount: overdueAmount, sub: `${overdueCount} Adet Gecikmiş Çek`, icon: 'pi-exclamation-triangle', theme: 'rose' },
                card3: { title: 'ÖDENEN / KAPANAN ÇEKLER', amount: paidAmount, sub: `${paidCount} Adet Bankadan Ödendi`, icon: 'pi-check-circle', theme: 'emerald' },
                card4: { title: 'TOPLAM FİRMA ÇEK HACMİ', amount: totalAmount, sub: `${totalCount} Adet Filtrelenen Çek`, icon: 'pi-wallet', theme: 'blue' }
            };
        } else if (activeTabIndex === 1) {
            // Müşteri Çekleri Metrics
            let portfolioCount = 0, portfolioAmount = 0;
            let overdueCount = 0, overdueAmount = 0;
            let endorsedCount = 0, endorsedAmount = 0;
            let collectedCount = 0, collectedAmount = 0;

            inboundCheques.forEach((c) => {
                const amount = Number(c.amount) || 0;
                if (c.status === 1) {
                    portfolioCount++;
                    portfolioAmount += amount;
                    if (c.dueDate && new Date(c.dueDate) < today) {
                        overdueCount++;
                        overdueAmount += amount;
                    }
                } else if (c.status === 2) {
                    endorsedCount++;
                    endorsedAmount += amount;
                } else if (c.status === 3) {
                    collectedCount++;
                    collectedAmount += amount;
                }
            });

            return {
                card1: { title: 'PORTFÖYDE / KASADA BEKLEYEN', amount: portfolioAmount, sub: `${portfolioCount} Adet Müşteri Çeki`, icon: 'pi-folder-open', theme: 'blue' },
                card2: { title: 'VADESİ GEÇEN TAHSİLATLAR', amount: overdueAmount, sub: `${overdueCount} Adet Gecikmiş Tahsilat`, icon: 'pi-exclamation-circle', theme: 'rose' },
                card3: { title: 'CİRO EDİLEN ÇEKLER', amount: endorsedAmount, sub: `${endorsedCount} Adet Tedarikçiye Devredildi`, icon: 'pi-sync', theme: 'amber' },
                card4: { title: 'TAHSİL EDİLEN ÇEKLER', amount: collectedAmount, sub: `${collectedCount} Adet Hesaba/Kasaya Girdi`, icon: 'pi-check-circle', theme: 'emerald' }
            };
        } else {
            // Tüm Çekler & Net Nakit Akışı Metrics
            let inPending = 0, inPendingCount = 0;
            let outPending = 0, outPendingCount = 0;

            allCheques.forEach((c) => {
                const amount = Number(c.amount) || 0;
                if (c.type === 1 && c.status === 1) {
                    inPending += amount;
                    inPendingCount++;
                } else if (c.type === 2 && (c.status === 2 || c.status === 1)) {
                    outPending += amount;
                    outPendingCount++;
                }
            });

            const netCashflow = inPending - outPending;

            return {
                card1: { title: 'BEKLEYEN GİRİŞLER (MÜŞTERİ)', amount: inPending, sub: `${inPendingCount} Adet Tahsil Edilecek`, icon: 'pi-arrow-down-left', theme: 'emerald' },
                card2: { title: 'BEKLEYEN ÇIKIŞLAR (FİRMA)', amount: outPending, sub: `${outPendingCount} Adet Ödenecek`, icon: 'pi-arrow-up-right', theme: 'rose' },
                card3: { title: 'NET ÇEK NAKİT ETKİSİ', amount: netCashflow, sub: netCashflow >= 0 ? 'Pozitif Nakit Girişi' : 'Ödeme Açığı (Finansman Gerekir)', icon: 'pi-chart-line', theme: netCashflow >= 0 ? 'emerald' : 'rose' },
                card4: { title: 'TOPLAM İŞLEM HACMİ', amount: inPending + outPending, sub: `${allCheques.length} Adet Filtrelenen Çek`, icon: 'pi-wallet', theme: 'blue' }
            };
        }
    }, [activeTabIndex, outboundCheques, inboundCheques, allCheques]);

    // ==========================================
    // ACTIONS & HANDLERS
    // ==========================================
    // 1. Yeni Firma Çeki Oluştur
    const handleAccountChangeForNewCheque = (accountId) => {
        const selectedOpt = accountOptions.find((o) => o.value === accountId);
        const accName = selectedOpt?.account?.name || '';
        setNewCheque((prev) => ({
            ...prev,
            financialAccountId: accountId,
            bankName: prev.bankName || accName
        }));
    };

    const saveNewCheque = async () => {
        if (!newCheque.amount || !newCheque.dueDate || !newCheque.chequeNumber) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen zorunlu alanları doldurun (Tutar, Vade Tarihi, Çek No)' });
            return;
        }

        try {
            setActionLoading(true);
            const res = await ChequeService.createCheque(newCheque);
            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Firma çeki tanımlandı.' });
                setShowCreateDialog(false);
                fetchCheques();
                setNewCheque({
                    amount: 0,
                    dueDate: null,
                    financialAccountId: null,
                    bankName: '',
                    bankBranch: '',
                    chequeNumber: '',
                    drawer: 'Kendi Çekimiz',
                    description: '',
                    type: 2
                });
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Kaydedilemedi' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message || 'Kayıt sırasında hata oluştu' });
        } finally {
            setActionLoading(false);
        }
    };

    // 2. Firma Çeki Ödeme Yap (Bankadan Düş)
    const openPayDialog = (cheque) => {
        setSelectedChequeToPay(cheque);
        setPayForm({
            financialAccountId: cheque.financialAccountId || (accountOptions.length > 0 ? accountOptions[0].value : null),
            transactionDate: new Date(),
            description: `${cheque.supplierName ? cheque.supplierName + ' - ' : ''}Çek Ödemesi (No: ${cheque.chequeNumber})`
        });
        setPayDialogVisible(true);
    };

    const handleConfirmPayment = async () => {
        if (!selectedChequeToPay) return;
        if (!payForm.financialAccountId) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen çıkış yapılacak banka hesabını seçin' });
            return;
        }

        try {
            setActionLoading(true);
            const res = await ChequeService.updateStatus({
                chequeId: selectedChequeToPay.id,
                newStatus: 4, // Paid
                financialAccountId: payForm.financialAccountId,
                transactionDate: payForm.transactionDate,
                description: payForm.description
            });

            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek ödendi olarak işaretlendi ve banka bakiyesinden düşüldü.' });
                setPayDialogVisible(false);
                fetchCheques();
                loadLookups();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'İşlem gerçekleştirilemedi.' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message || 'Ödeme kaydedilemedi.' });
        } finally {
            setActionLoading(false);
        }
    };

    // 3. Müşteri Çeki Tahsil Et (Kasaya/Bankaya Al)
    const openCollectDialog = (cheque) => {
        setSelectedChequeToCollect(cheque);
        setCollectForm({
            financialAccountId: cheque.financialAccountId || (accountOptions.length > 0 ? accountOptions[0].value : null),
            transactionDate: new Date(),
            description: `${cheque.customerName ? cheque.customerName + ' - ' : ''}Çek Tahsilatı (No: ${cheque.chequeNumber})`
        });
        setCollectDialogVisible(true);
    };

    const handleConfirmCollection = async () => {
        if (!selectedChequeToCollect) return;
        if (!collectForm.financialAccountId) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen tahsilatın yatırılacağı hesap/kasayı seçin' });
            return;
        }

        try {
            setActionLoading(true);
            const res = await ChequeService.updateStatus({
                chequeId: selectedChequeToCollect.id,
                newStatus: 3, // Collected
                financialAccountId: collectForm.financialAccountId,
                transactionDate: collectForm.transactionDate,
                description: collectForm.description
            });

            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek tahsil edildi ve seçilen hesaba giriş yapıldı.' });
                setCollectDialogVisible(false);
                fetchCheques();
                loadLookups();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Tahsilat gerçekleştirilemedi.' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message || 'Tahsilat kaydedilemedi.' });
        } finally {
            setActionLoading(false);
        }
    };

    // 4. Müşteri Çeki Tedarikçiye Ciro Et
    const openEndorseDialog = (cheque) => {
        setSelectedChequeToEndorse(cheque);
        setEndorseForm({
            supplierId: null,
            endorsementDate: new Date(),
            description: `${cheque.customerName ? cheque.customerName + ' ' : ''}Çek Cirosu (No: ${cheque.chequeNumber})`
        });
        setEndorseDialogVisible(true);
    };

    const handleConfirmEndorsement = async () => {
        if (!selectedChequeToEndorse) return;
        if (!endorseForm.supplierId) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen çeki ciro edeceğiniz tedarikçiyi seçin' });
            return;
        }

        try {
            setActionLoading(true);
            const res = await ChequeService.endorseCheque({
                chequeId: selectedChequeToEndorse.id,
                supplierId: endorseForm.supplierId,
                endorsementDate: endorseForm.endorsementDate,
                description: endorseForm.description
            });

            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek başarıyla tedarikçiye ciro edildi ve borçtan düşüldü.' });
                setEndorseDialogVisible(false);
                fetchCheques();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Ciro işlemi gerçekleştirilemedi.' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message || 'Ciro işlemi kaydedilemedi.' });
        } finally {
            setActionLoading(false);
        }
    };

    // 5. Çek Düzenleme Handlers
    const openEditDialog = (cheque) => {
        setSelectedChequeToEdit(cheque);
        setEditForm({
            id: cheque.id,
            customerId: cheque.customerId || null,
            financialAccountId: cheque.financialAccountId || null,
            amount: Number(cheque.amount) || 0,
            dueDate: cheque.dueDate ? new Date(cheque.dueDate) : new Date(),
            bankName: cheque.bankName || '',
            bankBranch: cheque.bankBranch || '',
            chequeNumber: cheque.chequeNumber || '',
            drawer: cheque.drawer || '',
            description: cheque.description || ''
        });
        setEditDialogVisible(true);
    };

    const handleConfirmEdit = async () => {
        if (!selectedChequeToEdit) return;
        if (!editForm.amount || editForm.amount <= 0) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen geçerli bir çek tutarı girin' });
            return;
        }
        if (!editForm.dueDate) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen vade tarihi seçin' });
            return;
        }
        if (!editForm.chequeNumber || !editForm.chequeNumber.trim()) {
            toast.current?.show({ severity: 'warn', summary: 'Uyarı', detail: 'Lütfen çek numarasını girin' });
            return;
        }

        try {
            setActionLoading(true);
            const res = await ChequeService.updateCheque({
                id: editForm.id,
                customerId: editForm.customerId,
                financialAccountId: editForm.financialAccountId,
                amount: editForm.amount,
                dueDate: editForm.dueDate,
                bankName: editForm.bankName,
                bankBranch: editForm.bankBranch,
                chequeNumber: editForm.chequeNumber,
                drawer: editForm.drawer,
                description: editForm.description
            });

            if (res.success) {
                toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek bilgileri başarıyla güncellendi.' });
                setEditDialogVisible(false);
                fetchCheques();
            } else {
                toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Çek güncellenemedi.' });
            }
        } catch (err) {
            toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message || 'Çek güncellenirken hata oluştu.' });
        } finally {
            setActionLoading(false);
        }
    };

    // 6. Toplu Banka Hesabı Eşleştirme Kaydet
    const handleSaveUnlinkedMappings = async () => {
        const toUpdate = unlinkedChequesList.filter((item) => item.targetAccountId);
        if (toUpdate.length === 0) {
            toast.current?.show({
                severity: 'info',
                summary: 'Bilgi',
                detail: 'Hiçbir çek için banka hesabı seçilmedi.'
            });
            setUnlinkedModalVisible(false);
            return;
        }

        try {
            setSavingUnlinked(true);
            const updatePromises = toUpdate.map((item) => {
                const selectedAcc = accountOptions.find((a) => a.value === item.targetAccountId)?.account;
                return ChequeService.updateCheque({
                    id: item.id,
                    customerId: item.customerId,
                    financialAccountId: item.targetAccountId,
                    amount: Number(item.amount) || 0,
                    dueDate: item.dueDate,
                    bankName: selectedAcc?.name ? selectedAcc.name : item.bankName,
                    bankBranch: item.bankBranch,
                    chequeNumber: item.chequeNumber,
                    drawer: item.drawer,
                    description: item.description
                });
            });

            await Promise.all(updatePromises);

            toast.current?.show({
                severity: 'success',
                summary: 'Başarılı',
                detail: `${toUpdate.length} adet firma çeki başarıyla banka hesabına bağlandı.`
            });
            setUnlinkedModalVisible(false);
            fetchCheques();
        } catch (err) {
            toast.current?.show({
                severity: 'error',
                summary: 'Hata',
                detail: err.message || 'Eşleştirme kaydedilirken hata oluştu'
            });
        } finally {
            setSavingUnlinked(false);
        }
    };

    // 5. İade Etme & Portföye Geri Alma
    const handleRevertStatus = (cheque, targetStatus = 1) => {
        const isPaid = cheque.status === 4;
        const isCollected = cheque.status === 3;
        const isEndorsed = cheque.status === 2;

        let promptMessage = `${cheque.chequeNumber} nolu çeki portföye geri almak istediğinize emin misiniz?`;
        if (isPaid) {
            promptMessage = `${cheque.chequeNumber} nolu çekin ödeme işlemini geri almak istediğinize emin misiniz? (Banka bakiyesine tutar iade edilecektir)`;
        } else if (isCollected) {
            promptMessage = `${cheque.chequeNumber} nolu çekin tahsilatını geri almak istediğinize emin misiniz? (Hesap bakiyesinden tutar düşülecektir)`;
        } else if (isEndorsed) {
            promptMessage = `${cheque.chequeNumber} nolu çekin ciro işlemini iptal edip portföye almak istediğinize emin misiniz?`;
        }

        confirmDialog({
            message: promptMessage,
            header: 'İşlem Onayı',
            icon: 'pi pi-exclamation-triangle',
            acceptLabel: 'Evet, Geri Al',
            rejectLabel: 'Vazgeç',
            acceptClassName: 'p-button-danger !rounded-xl',
            rejectClassName: 'p-button-text !rounded-xl',
            accept: async () => {
                try {
                    setLoading(true);
                    const res = await ChequeService.updateStatus({
                        chequeId: cheque.id,
                        newStatus: targetStatus
                    });
                    if (res.success) {
                        toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek durumu güncellendi.' });
                        fetchCheques();
                        loadLookups();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Güncellenemedi' });
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    const handleReturnCheque = (cheque) => {
        confirmDialog({
            message: `${cheque.chequeNumber} nolu çeki iade edildi olarak işaretlemek istediğinize emin misiniz?`,
            header: 'İade Onayı',
            icon: 'pi pi-exclamation-circle',
            acceptLabel: 'Evet, İade Et',
            rejectLabel: 'Vazgeç',
            acceptClassName: 'p-button-danger !rounded-xl',
            rejectClassName: 'p-button-text !rounded-xl',
            accept: async () => {
                try {
                    setLoading(true);
                    const res = await ChequeService.updateStatus({
                        chequeId: cheque.id,
                        newStatus: 5 // Returned
                    });
                    if (res.success) {
                        toast.current?.show({ severity: 'success', summary: 'Başarılı', detail: 'Çek iade edildi olarak işaretlendi.' });
                        fetchCheques();
                    } else {
                        toast.current?.show({ severity: 'error', summary: 'Hata', detail: res.message || 'Güncellenemedi' });
                    }
                } catch (err) {
                    toast.current?.show({ severity: 'error', summary: 'Hata', detail: err.message });
                } finally {
                    setLoading(false);
                }
            }
        });
    };

    // ==========================================
    // COLUMN TEMPLATES
    // ==========================================
    const dueDateBodyTemplate = (row) => {
        const isClosed = row.status === 3 || row.status === 4 || row.status === 5;
        const due = new Date(row.dueDate);
        const dateStr = due.toLocaleDateString('tr-TR');

        if (isClosed) {
            return (
                <div className="flex items-center gap-1.5 text-slate-400 text-xs font-semibold">
                    <i className="pi pi-calendar text-[11px]" />
                    <span>{dateStr}</span>
                </div>
            );
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        due.setHours(0, 0, 0, 0);
        const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            const daysAgo = Math.abs(diffDays);
            return (
                <div className="flex flex-col gap-1">
                    <span className="font-black text-rose-600 dark:text-rose-400 text-xs flex items-center gap-1.5">
                        <i className="pi pi-exclamation-triangle text-xs text-rose-500 animate-pulse" />
                        <span>{dateStr}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 w-fit bg-rose-100 dark:bg-rose-900/60 text-rose-700 dark:text-rose-200 text-[10px] font-black px-2 py-0.5 rounded-md shadow-xs">
                        <span className="w-1.5 h-1.5 rounded-full bg-rose-500 animate-ping"></span>
                        <span>{daysAgo} gün gecikti</span>
                    </span>
                </div>
            );
        } else if (diffDays === 0) {
            return (
                <div className="flex flex-col gap-1">
                    <span className="font-black text-amber-600 dark:text-amber-400 text-xs flex items-center gap-1.5">
                        <i className="pi pi-clock text-xs text-amber-500" />
                        <span>{dateStr}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 w-fit bg-amber-100 dark:bg-amber-900/60 text-amber-800 dark:text-amber-200 text-[10px] font-black px-2 py-0.5 rounded-md border border-amber-300 dark:border-amber-700 shadow-xs animate-pulse">
                        <span className="w-1.5 h-1.5 rounded-full bg-amber-500"></span>
                        <span>Bugün Vadesi!</span>
                    </span>
                </div>
            );
        } else if (diffDays <= 7) {
            return (
                <div className="flex flex-col gap-1">
                    <span className="font-bold text-amber-700 dark:text-amber-300 text-xs flex items-center gap-1.5">
                        <i className="pi pi-calendar-clock text-xs text-amber-500" />
                        <span>{dateStr}</span>
                    </span>
                    <span className="inline-flex items-center gap-1 w-fit bg-amber-50 dark:bg-amber-950/50 text-amber-700 dark:text-amber-300 border border-amber-200 dark:border-amber-800 text-[10px] font-bold px-2 py-0.5 rounded-md">
                        <span>{diffDays} gün kaldı</span>
                    </span>
                </div>
            );
        }

        return (
            <div className="flex items-center gap-1.5 text-slate-700 dark:text-slate-300 text-xs font-bold">
                <i className="pi pi-calendar text-[11px] text-slate-400" />
                <span>{dateStr}</span>
            </div>
        );
    };

    const statusBodyTemplate = (rowData) => {
        const statuses = {
            1: { label: rowData.type === 2 ? 'Portföyde (Verilmedi)' : 'Kasada / Portföyde', severity: 'info' },
            2: { label: rowData.type === 2 ? 'Verildi (Ödeme Bekliyor)' : 'Ciro Edildi', severity: 'warning' },
            3: { label: 'Tahsil Edildi', severity: 'success' },
            4: { label: 'Ödendi (Kapandı)', severity: 'success' },
            5: { label: 'İade Edildi', severity: 'danger' }
        };
        const status = statuses[rowData.status] || { label: 'Bilinmiyor', severity: 'secondary' };
        return <Tag value={status.label} severity={status.severity} className="font-bold text-[11px] px-2.5 py-1" />;
    };

    const typeBodyTemplate = (rowData) => {
        return rowData.type === 2 ? (
            <Tag value="Firma Çeki" severity="warning" className="font-bold text-[10px]" />
        ) : (
            <Tag value="Müşteri Çeki" severity="info" className="font-bold text-[10px]" />
        );
    };

    const bankAccountBodyTemplate = (row) => {
        return (
            <div className="flex flex-col gap-0.5">
                {row.financialAccountName ? (
                    <span className="font-bold text-slate-800 dark:text-slate-200 text-xs flex items-center gap-1.5">
                        <i className="pi pi-building text-blue-600 text-xs" />
                        <span>{row.financialAccountName}</span>
                    </span>
                ) : (
                    <div className="flex items-center gap-1.5 flex-wrap">
                        <span className="text-slate-700 dark:text-slate-300 font-medium text-xs">
                            {row.bankName || "-"}
                        </span>
                        {row.type === 2 && !row.financialAccountId && (
                            <span className="text-[9px] text-amber-700 dark:text-amber-300 bg-amber-50 dark:bg-amber-950/70 border border-amber-200/80 dark:border-amber-800/60 rounded px-1.5 py-0.5 font-bold">
                                Hesap Bağlı Değil
                            </span>
                        )}
                    </div>
                )}
                {row.bankBranch && (
                    <span className="text-[10px] text-slate-400 font-normal">
                        Şube: {row.bankBranch}
                    </span>
                )}
            </div>
        );
    };

    const outboundActionBodyTemplate = (rowData) => {
        return (
            <div className="flex items-center gap-1.5">
                {rowData.status === 2 && (
                    <>
                        <Button
                            icon="pi pi-check-circle"
                            label="Ödendi"
                            className="p-button-sm !bg-emerald-600 hover:!bg-emerald-700 border-none !rounded-xl !py-1.5 !px-3 font-bold text-xs text-white shadow-sm"
                            onClick={() => openPayDialog(rowData)}
                            tooltip="Bankadan Çıkış Yap / Ödendi İşaretle"
                            tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                            icon="pi pi-undo"
                            className="p-button-sm p-button-secondary p-button-outlined !rounded-xl !p-2"
                            onClick={() => handleRevertStatus(rowData, 1)}
                            tooltip="Portföye Geri Al"
                            tooltipOptions={{ position: 'top' }}
                        />
                    </>
                )}
                {rowData.status === 1 && (
                    <>
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-sm p-button-warning p-button-outlined !rounded-xl !p-2"
                            onClick={() => openEditDialog(rowData)}
                            tooltip="Çek Bilgilerini Düzenle"
                            tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                            icon="pi pi-ban"
                            className="p-button-sm p-button-danger p-button-outlined !rounded-xl !p-2"
                            onClick={() => handleReturnCheque(rowData)}
                            tooltip="İptal / İade Et"
                            tooltipOptions={{ position: 'top' }}
                        />
                    </>
                )}
                {rowData.status === 4 && (
                    <Button
                        icon="pi pi-replay"
                        label="Geri Al"
                        className="p-button-sm p-button-secondary p-button-outlined !rounded-xl !py-1.5 !px-2.5 font-bold text-xs"
                        onClick={() => handleRevertStatus(rowData, 1)}
                        tooltip="Ödemeyi İptal Et / Portföye Al"
                        tooltipOptions={{ position: 'top' }}
                    />
                )}
            </div>
        );
    };

    const inboundActionBodyTemplate = (rowData) => {
        return (
            <div className="flex items-center gap-1.5">
                {rowData.status === 1 && (
                    <>
                        <Button
                            icon="pi pi-money-bill"
                            label="Tahsil Et"
                            className="p-button-sm !bg-emerald-600 hover:!bg-emerald-700 border-none !rounded-xl !py-1.5 !px-2.5 font-bold text-xs text-white shadow-sm"
                            onClick={() => openCollectDialog(rowData)}
                            tooltip="Hesaba/Kasaya Tahsil Et"
                            tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                            icon="pi pi-sync"
                            label="Ciro Et"
                            className="p-button-sm !bg-blue-600 hover:!bg-blue-700 border-none !rounded-xl !py-1.5 !px-2.5 font-bold text-xs text-white shadow-sm"
                            onClick={() => openEndorseDialog(rowData)}
                            tooltip="Tedarikçiye Ciro Et"
                            tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                            icon="pi pi-pencil"
                            className="p-button-sm p-button-warning p-button-outlined !rounded-xl !p-2"
                            onClick={() => openEditDialog(rowData)}
                            tooltip="Çek Bilgilerini Düzenle"
                            tooltipOptions={{ position: 'top' }}
                        />
                        <Button
                            icon="pi pi-replay"
                            className="p-button-sm p-button-danger p-button-outlined !rounded-xl !p-2"
                            onClick={() => handleReturnCheque(rowData)}
                            tooltip="Müşteriye İade Et"
                            tooltipOptions={{ position: 'top' }}
                        />
                    </>
                )}
                {(rowData.status === 2 || rowData.status === 3) && (
                    <Button
                        icon="pi pi-replay"
                        label="Geri Al"
                        className="p-button-sm p-button-secondary p-button-outlined !rounded-xl !py-1.5 !px-2.5 font-bold text-xs"
                        onClick={() => handleRevertStatus(rowData, 1)}
                        tooltip="İşlemi İptal Et / Kasaya Al"
                        tooltipOptions={{ position: 'top' }}
                    />
                )}
            </div>
        );
    };

    const rowClassName = (row) => {
        if (Number(row.id) === highlightedChequeId) {
            return '!bg-blue-50/90 dark:!bg-blue-950/40 ring-2 ring-blue-400';
        }

        const isClosed = row.status === 3 || row.status === 4 || row.status === 5;
        if (isClosed) {
            return 'opacity-65 hover:opacity-100 transition-opacity';
        }

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const due = new Date(row.dueDate);
        due.setHours(0, 0, 0, 0);

        const diffDays = Math.round((due.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

        if (diffDays < 0) {
            // Vadesi geçmiş (Kırmızı arka plan ve sol kırmızı çizgi)
            return '!bg-rose-50/75 dark:!bg-rose-950/25 hover:!bg-rose-100/90 dark:hover:!bg-rose-950/45 border-l-4 !border-l-rose-500 font-medium';
        } else if (diffDays === 0) {
            // Vadesi bugün (Sarı arka plan ve sol amber çizgi)
            return '!bg-amber-50/85 dark:!bg-amber-950/30 hover:!bg-amber-100/90 dark:hover:!bg-amber-950/50 border-l-4 !border-l-amber-500 font-medium';
        } else if (diffDays <= 7) {
            // Vadesi 7 gün içinde yaklaşan (Hafif amber ve sol açık amber çizgi)
            return '!bg-amber-50/40 dark:!bg-amber-950/15 hover:!bg-amber-100/70 dark:hover:!bg-amber-950/35 border-l-4 !border-l-amber-400';
        }

        return '';
    };

    const renderHeader = (isOutboundTab) => {
        return (
            <div className="flex flex-col sm:flex-row justify-between items-center gap-3">
                {isOutboundTab && (
                    <Button
                        label="Yeni Firma Çeki Tanımla"
                        icon="pi pi-plus"
                        className="p-button-sm !rounded-xl !bg-blue-600 hover:!bg-blue-700 border-none font-bold text-xs py-2.5 px-4 shadow-sm text-white"
                        onClick={() => setShowCreateDialog(true)}
                    />
                )}
                {!isOutboundTab && <div />}
                <span className="p-input-icon-left w-full sm:w-auto">
                    <i className="pi pi-search" />
                    <InputText
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        placeholder="Müşteri, tedarikçi, banka veya çek no ara..."
                        className="p-inputtext-sm !py-2.5 !rounded-xl text-xs font-bold w-full sm:w-72"
                    />
                </span>
            </div>
        );
    };

    // Helper for KPI theme colors
    const getThemeStyles = (theme) => {
        switch (theme) {
            case 'amber':
                return {
                    bg: 'bg-amber-50/60 dark:bg-amber-950/20',
                    border: 'border-amber-200/80 dark:border-amber-900/40',
                    title: 'text-amber-600 dark:text-amber-400',
                    iconBg: 'bg-amber-100 dark:bg-amber-900/60',
                    iconColor: 'text-amber-600 dark:text-amber-400',
                    amount: 'text-slate-800 dark:text-white',
                    sub: 'text-amber-600/90 dark:text-amber-400/90'
                };
            case 'rose':
                return {
                    bg: 'bg-rose-50/60 dark:bg-rose-950/20',
                    border: 'border-rose-200/80 dark:border-rose-900/40',
                    title: 'text-rose-600 dark:text-rose-400',
                    iconBg: 'bg-rose-100 dark:bg-rose-900/60',
                    iconColor: 'text-rose-600 dark:text-rose-400',
                    amount: 'text-rose-600 dark:text-rose-400',
                    sub: 'text-rose-600/90 dark:text-rose-400/90'
                };
            case 'emerald':
                return {
                    bg: 'bg-emerald-50/60 dark:bg-emerald-950/20',
                    border: 'border-emerald-200/80 dark:border-emerald-900/40',
                    title: 'text-emerald-600 dark:text-emerald-400',
                    iconBg: 'bg-emerald-100 dark:bg-emerald-900/60',
                    iconColor: 'text-emerald-600 dark:text-emerald-400',
                    amount: 'text-slate-800 dark:text-white',
                    sub: 'text-emerald-600/90 dark:text-emerald-400/90'
                };
            case 'blue':
            default:
                return {
                    bg: 'bg-blue-50/60 dark:bg-blue-950/20',
                    border: 'border-blue-200/80 dark:border-blue-900/40',
                    title: 'text-blue-600 dark:text-blue-400',
                    iconBg: 'bg-blue-100 dark:bg-blue-900/60',
                    iconColor: 'text-blue-600 dark:text-blue-400',
                    amount: 'text-slate-800 dark:text-white',
                    sub: 'text-blue-600/90 dark:text-blue-400/90'
                };
        }
    };

    return (
        <div className="p-4 space-y-4 animate-fade-in">
            <Toast ref={toast} appendTo={mounted ? document.body : "self"} />
            <ConfirmDialog />

            <div className="bg-white dark:bg-slate-900 shadow-xl border border-slate-200 dark:border-slate-800 rounded-3xl overflow-hidden p-6">
                {/* Header */}
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
                    <div className="flex items-center gap-3">
                        <div
                            className="!bg-blue-600 rounded-2xl shadow-lg shadow-blue-600/20 text-white flex items-center justify-center flex-shrink-0"
                            style={{ width: '48px', height: '48px', minWidth: '48px', maxWidth: '48px', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}
                        >
                            <i className="pi pi-id-card text-white text-xl"></i>
                        </div>
                        <div>
                            <h1 className="text-xl font-black text-slate-800 dark:text-white m-0">Çek Yönetimi</h1>
                            <p className="text-xs text-slate-500 dark:text-slate-400 font-medium m-0 mt-0.5">
                                Kendi firma çeklerimiz, müşteri çekleri, ciro ve nakit akışı portföy takibi
                            </p>
                        </div>
                    </div>
                    <Button
                        icon="pi pi-refresh"
                        className="p-button-text p-button-secondary rounded-xl hover:bg-slate-100 dark:hover:bg-slate-800"
                        onClick={() => fetchCheques()}
                        loading={loading}
                        tooltip="Yenile"
                    />
                </div>

                {/* Unlinked Company Cheques Alert Banner */}
                {unlinkedCompanyCheques.length > 0 && (
                    <div
                        className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3.5 bg-amber-500/10 border border-amber-500/30 rounded-2xl mb-5 animate-fade-in"
                        style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: '16px' }}
                    >
                        <div className="flex items-center gap-3 flex-1" style={{ display: 'flex', alignItems: 'center', gap: '12px', flex: 1 }}>
                            <div
                                className="rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-md shadow-amber-500/20"
                                style={{ width: '40px', height: '40px', minWidth: '40px', maxWidth: '40px', flexShrink: 0 }}
                            >
                                <i className="pi pi-exclamation-triangle text-lg" />
                            </div>
                            <div className="flex flex-col flex-1" style={{ flex: 1 }}>
                                <h4 className="text-xs font-black text-amber-900 dark:text-amber-200 m-0">
                                    Banka Hesabı Bağlantısı Eksik Çekler ({unlinkedCompanyCheques.length} Adet)
                                </h4>
                                <p className="text-[11px] text-amber-700 dark:text-amber-300 font-medium m-0 mt-0.5">
                                    Firma çeklerinizin ödendiğinde banka bakiyenizden otomatik düşülebilmesi için ilgili banka hesaplarını eşleştirin.
                                </p>
                            </div>
                        </div>
                        <Button
                            label="Hesapları Eşleştir"
                            icon="pi pi-link"
                            className="!bg-amber-600 hover:!bg-amber-700 border-none !rounded-xl text-xs font-bold text-white shadow-sm shrink-0 !py-2.5 !px-4"
                            style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                            onClick={() => {
                                setUnlinkedChequesList(
                                    unlinkedCompanyCheques.map((c) => ({
                                        ...c,
                                        targetAccountId: c.financialAccountId || null
                                    }))
                                );
                                setUnlinkedModalVisible(true);
                            }}
                        />
                    </div>
                )}

                {/* Period Filter Pill Bar & Status Dropdown */}
                <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3 mb-5 p-2 bg-slate-50 dark:bg-slate-800/60 rounded-2xl border border-slate-200/80 dark:border-slate-700/80">
                    {/* Period Buttons Group */}
                    <div className="flex flex-wrap items-center gap-1.5 p-1 bg-slate-200/60 dark:bg-slate-900/80 rounded-xl border border-slate-300/50 dark:border-slate-800">
                        {periodButtons.map((btn) => {
                            const isActive = period === btn.value;

                            let btnClasses = "inline-flex items-center gap-2 px-3.5 py-2 rounded-lg text-xs font-bold transition-all duration-150 cursor-pointer select-none border-none outline-none ";
                            if (isActive) {
                                switch (btn.value) {
                                    case 'overdue':
                                        btnClasses += "!bg-rose-600 text-white shadow-sm font-black";
                                        break;
                                    case 'today':
                                        btnClasses += "!bg-amber-500 text-white shadow-sm font-black";
                                        break;
                                    case 'week':
                                        btnClasses += "!bg-cyan-600 text-white shadow-sm font-black";
                                        break;
                                    case 'month':
                                        btnClasses += "!bg-indigo-600 text-white shadow-sm font-black";
                                        break;
                                    case 'custom':
                                        btnClasses += "!bg-emerald-600 text-white shadow-sm font-black";
                                        break;
                                    case 'all':
                                    default:
                                        btnClasses += "!bg-blue-600 text-white shadow-sm font-black";
                                        break;
                                }
                            } else {
                                btnClasses += "text-slate-600 dark:text-slate-300 hover:text-slate-900 dark:hover:text-white hover:bg-white/60 dark:hover:bg-slate-800/60";
                            }

                            let badgeClasses = "text-[11px] px-2 py-0.5 rounded-full font-black leading-none transition-all ";
                            if (isActive) {
                                badgeClasses += "bg-white/20 text-white";
                            } else {
                                if (btn.value === 'overdue' && btn.count > 0) {
                                    badgeClasses += "bg-rose-100 text-rose-700 dark:bg-rose-950 dark:text-rose-300 border border-rose-300 dark:border-rose-800 animate-pulse";
                                } else if (btn.value === 'today' && btn.count > 0) {
                                    badgeClasses += "bg-amber-100 text-amber-800 dark:bg-amber-950 dark:text-amber-300 border border-amber-300 dark:border-amber-800";
                                } else if (btn.value === 'week' && btn.count > 0) {
                                    badgeClasses += "bg-cyan-100 text-cyan-800 dark:bg-cyan-950 dark:text-cyan-300 border border-cyan-300 dark:border-cyan-800";
                                } else if (btn.value === 'month' && btn.count > 0) {
                                    badgeClasses += "bg-indigo-100 text-indigo-800 dark:bg-indigo-950 dark:text-indigo-300 border border-indigo-300 dark:border-indigo-800";
                                } else {
                                    badgeClasses += "bg-slate-300/80 text-slate-700 dark:bg-slate-700 dark:text-slate-200";
                                }
                            }

                            return (
                                <button
                                    key={btn.value}
                                    type="button"
                                    style={{ border: 'none', outline: 'none' }}
                                    onClick={() => {
                                        setPeriod(btn.value);
                                        if (btn.value !== 'custom') {
                                            setStartDate(null);
                                            setEndDate(null);
                                        }
                                    }}
                                    className={btnClasses}
                                >
                                    <i className={`pi ${btn.icon} text-xs`} />
                                    <span>{btn.label}</span>
                                    {btn.count !== undefined && (
                                        <span className={badgeClasses}>
                                            {btn.count}
                                        </span>
                                    )}
                                </button>
                            );
                        })}
                    </div>

                    {/* Status Dropdown & Reset */}
                    <div className="flex items-center gap-2">
                        <Dropdown
                            value={statusFilter}
                            options={statusOptions}
                            onChange={(e) => setStatusFilter(e.value)}
                            placeholder="Tüm Durumlar"
                            appendTo={mounted ? document.body : "self"}
                            className="rounded-xl border-slate-200 dark:border-slate-700 text-xs font-bold w-48 h-[38px] flex items-center bg-white dark:bg-slate-800 shadow-sm"
                        />

                        {(period !== "all" || statusFilter !== null || startDate || endDate || globalFilter) && (
                            <Button
                                icon="pi pi-filter-slash"
                                label="Temizle"
                                outlined
                                severity="secondary"
                                className="!rounded-xl !text-xs !font-black !py-2 !h-[38px]"
                                onClick={clearFilters}
                                tooltip="Filtreleri Sıfırla"
                            />
                        )}
                    </div>
                </div>

                {/* Custom Date Range Picker (Shown when 'Özel Tarih' is selected) */}
                {period === "custom" && (
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-3 bg-slate-50 dark:bg-slate-900/60 p-4 rounded-2xl border border-slate-200 dark:border-slate-800 animate-fade-in items-end mb-5">
                        <div className="md:col-span-4 flex flex-col gap-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">
                                Başlangıç Vade Tarihi
                            </label>
                            <Calendar
                                value={startDate}
                                onChange={(e) => setStartDate(e.value)}
                                showIcon
                                dateFormat="dd.mm.yy"
                                placeholder="Tarih seçin..."
                                appendTo={mounted ? document.body : "self"}
                                className="w-full"
                                inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2 font-bold text-xs bg-white dark:bg-slate-800"
                            />
                        </div>
                        <div className="md:col-span-4 flex flex-col gap-1">
                            <label className="text-[10px] font-black text-slate-400 uppercase tracking-wider ml-1">
                                Bitiş Vade Tarihi
                            </label>
                            <Calendar
                                value={endDate}
                                onChange={(e) => setEndDate(e.value)}
                                showIcon
                                dateFormat="dd.mm.yy"
                                placeholder="Tarih seçin..."
                                appendTo={mounted ? document.body : "self"}
                                className="w-full"
                                inputClassName="rounded-xl border-slate-200 dark:border-slate-700 py-2 font-bold text-xs bg-white dark:bg-slate-800"
                            />
                        </div>
                        <div className="md:col-span-4 flex gap-2">
                            <Button
                                label="Tarihleri Sıfırla"
                                icon="pi pi-times"
                                outlined
                                severity="secondary"
                                className="w-full !rounded-xl !text-xs !font-black !py-2"
                                onClick={() => {
                                    setStartDate(null);
                                    setEndDate(null);
                                }}
                            />
                        </div>
                    </div>
                )}

                {/* Dynamic KPI Cards (Adapts to Active Tab) */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                    {[metrics.card1, metrics.card2, metrics.card3, metrics.card4].map((c, idx) => {
                        const style = getThemeStyles(c.theme);
                        return (
                            <div key={idx} className={`${style.bg} border ${style.border} rounded-2xl p-4 shadow-sm relative overflow-hidden flex flex-col justify-between`}>
                                <div className="flex items-center justify-between gap-2 mb-2">
                                    <span className={`text-[10px] font-black ${style.title} uppercase tracking-wider`}>
                                        {c.title}
                                    </span>
                                    <div
                                        className={`rounded-xl ${style.iconBg} ${style.iconColor}`}
                                        style={{
                                            width: '28px',
                                            height: '28px',
                                            minWidth: '28px',
                                            maxWidth: '28px',
                                            minHeight: '28px',
                                            maxHeight: '28px',
                                            flexShrink: 0,
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'center'
                                        }}
                                    >
                                        <i className={`pi ${c.icon} text-xs`}></i>
                                    </div>
                                </div>
                                <div>
                                    <div className={`text-lg font-black ${style.amount}`}>
                                        ₺{(Number(c.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                    </div>
                                    <p className={`text-[11px] font-bold m-0 mt-0.5 ${style.sub}`}>
                                        {c.sub}
                                    </p>
                                </div>
                            </div>
                        );
                    })}
                </div>

                {/* Tab Navigation */}
                <TabView
                    className="compact-tabview custom-finance-tabs"
                    activeIndex={activeTabIndex}
                    onTabChange={(e) => setActiveTabIndex(e.index)}
                >
                    {/* 🏢 SEKME 1: KENDİ ÇEKLERİMİZ (FİRMA ÇEKLERİ) */}
                    <TabPanel header="Kendi Çeklerimiz (Verilen)" leftIcon="pi pi-credit-card mr-2">
                        <DataTable
                            value={outboundCheques}
                            loading={loading}
                            paginator
                            rows={10}
                            globalFilter={globalFilter}
                            header={renderHeader(true)}
                            dataKey="id"
                            rowClassName={rowClassName}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mt-2"
                        >
                            <Column field="issueDate" header="Tanımlama Tarihi" body={(row) => new Date(row.issueDate).toLocaleDateString('tr-TR')} sortable />
                            <Column field="dueDate" header="Vade Tarihi & Durumu" body={dueDateBodyTemplate} sortable />
                            <Column field="financialAccountName" header="Banka / Bağlı Hesap" body={bankAccountBodyTemplate} sortable />
                            <Column field="chequeNumber" header="Çek No" sortable />
                            <Column field="supplierName" header="Verilen Tedarikçi" sortable body={(row) => row.supplierName || <span className="text-slate-400 font-normal">Portföyde (Verilmedi)</span>} />
                            <Column field="amount" header="Tutar" body={(row) => `₺${(Number(row.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={outboundActionBodyTemplate} />
                        </DataTable>
                    </TabPanel>

                    {/* 📥 SEKME 2: MÜŞTERİ ÇEKLERİ (ALINAN) */}
                    <TabPanel header="Müşteri Çekleri (Alınan)" leftIcon="pi pi-arrow-down-left mr-2">
                        <DataTable
                            value={inboundCheques}
                            loading={loading}
                            paginator
                            rows={10}
                            globalFilter={globalFilter}
                            header={renderHeader(false)}
                            dataKey="id"
                            rowClassName={rowClassName}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mt-2"
                        >
                            <Column field="dueDate" header="Vade Tarihi & Durumu" body={dueDateBodyTemplate} sortable />
                            <Column field="customerName" header="Alınan Müşteri" sortable body={(row) => row.customerName || '-'} />
                            <Column field="bankName" header="Banka / Keşideci" sortable body={(row) => (
                                <div className="flex flex-col">
                                    <span className="font-bold text-slate-800 dark:text-slate-200 text-xs">{row.bankName || "-"}</span>
                                    {row.drawer && <span className="text-[10px] text-slate-400">Keşideci: {row.drawer}</span>}
                                </div>
                            )} />
                            <Column field="chequeNumber" header="Çek No" sortable />
                            <Column field="supplierName" header="Ciro Edilen Tedarikçi" sortable body={(row) => row.supplierName ? (
                                <span className="font-bold text-amber-700 dark:text-amber-400 text-xs flex items-center gap-1">
                                    <i className="pi pi-sync text-[10px]" />
                                    <span>{row.supplierName}</span>
                                </span>
                            ) : (
                                <span className="text-slate-400 font-normal">Portföyde / Kasada</span>
                            )} />
                            <Column field="amount" header="Tutar" body={(row) => `₺${(Number(row.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                            <Column header="İşlem" body={inboundActionBodyTemplate} />
                        </DataTable>
                    </TabPanel>

                    {/* 📊 SEKME 3: TÜM ÇEKLER & NAKİT AKIŞI */}
                    <TabPanel header="Tüm Çekler & Nakit Akışı" leftIcon="pi pi-chart-bar mr-2">
                        <DataTable
                            value={allCheques}
                            loading={loading}
                            paginator
                            rows={10}
                            globalFilter={globalFilter}
                            header={renderHeader(false)}
                            dataKey="id"
                            rowClassName={rowClassName}
                            responsiveLayout="scroll"
                            className="p-datatable-sm mt-2"
                        >
                            <Column header="Tür" body={typeBodyTemplate} sortable sortField="type" />
                            <Column field="dueDate" header="Vade Tarihi & Durumu" body={dueDateBodyTemplate} sortable />
                            <Column header="İlgili Cari (Müşteri / Tedarikçi)" body={(row) => row.customerName || row.supplierName || '-'} sortable />
                            <Column field="bankName" header="Banka / Hesap" body={bankAccountBodyTemplate} sortable />
                            <Column field="chequeNumber" header="Çek No" sortable />
                            <Column field="amount" header="Tutar" body={(row) => (
                                <span className={`font-black ${row.type === 1 ? 'text-emerald-600 dark:text-emerald-400' : 'text-rose-600 dark:text-rose-400'}`}>
                                    {row.type === 1 ? '+ ' : '- '}
                                    ₺{(Number(row.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                            )} sortable />
                            <Column field="status" header="Durum" body={statusBodyTemplate} sortable />
                        </DataTable>
                    </TabPanel>
                </TabView>
            </div>

            {/* ========================================== */}
            {/* 1. YENİ FİRMA ÇEKİ TANIMLA DİALOG */}
            {/* ========================================== */}
            <Dialog
                header="Yeni Firma Çeki Tanımla"
                visible={showCreateDialog}
                style={{ width: '480px' }}
                modal
                onHide={() => setShowCreateDialog(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="İptal" icon="pi pi-times" onClick={() => setShowCreateDialog(false)} className="p-button-text !rounded-xl" disabled={actionLoading} />
                        <Button label="Kaydet" icon="pi pi-check" onClick={saveNewCheque} loading={actionLoading} className="p-button-primary !rounded-xl font-bold text-xs" />
                    </div>
                }
            >
                <div className="flex flex-col gap-4 mt-2">
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Bağlı Banka Hesabı (Sistem Kayıtlı)</label>
                        <Dropdown
                            value={newCheque.financialAccountId}
                            options={accountOptions}
                            onChange={(e) => handleAccountChangeForNewCheque(e.value)}
                            placeholder="Banka Hesabı Seçin (Opsiyonel)"
                            showClear
                            className="w-full rounded-xl border-slate-200 font-bold text-xs"
                            appendTo={mounted ? document.body : "self"}
                        />
                        <span className="text-[10px] text-slate-400">Çekin kesildiği banka hesabını seçerseniz, vadesinde tek tıkla ilgili hesaptan otomatik çıkış yapılır.</span>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Çek Numarası <span className="text-red-500">*</span></label>
                            <InputText value={newCheque.chequeNumber} onChange={(e) => setNewCheque({ ...newCheque, chequeNumber: e.target.value })} placeholder="Örn: 001245" className="!py-2.5 !rounded-xl text-xs font-bold" />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Vade Tarihi <span className="text-red-500">*</span></label>
                            <Calendar value={newCheque.dueDate} onChange={(e) => setNewCheque({ ...newCheque, dueDate: e.value })} dateFormat="dd.mm.yy" showIcon className="!rounded-xl" inputClassName="!py-2.5 text-xs font-bold" appendTo={mounted ? document.body : "self"} />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2.5">
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Banka Adı</label>
                            <InputText value={newCheque.bankName} onChange={(e) => setNewCheque({ ...newCheque, bankName: e.target.value })} placeholder="Örn: Garanti BBVA" className="!py-2.5 !rounded-xl text-xs" />
                        </div>
                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Banka Şubesi</label>
                            <InputText value={newCheque.bankBranch} onChange={(e) => setNewCheque({ ...newCheque, bankBranch: e.target.value })} placeholder="Örn: Kadıköy Şb." className="!py-2.5 !rounded-xl text-xs" />
                        </div>
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Tutar (TL) <span className="text-red-500">*</span></label>
                        <InputNumber value={newCheque.amount} onValueChange={(e) => setNewCheque({ ...newCheque, amount: e.value })} mode="currency" currency="TRY" locale="tr-TR" className="!rounded-xl" inputClassName="!py-2.5 font-bold text-sm" />
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Açıklama</label>
                        <InputText value={newCheque.description} onChange={(e) => setNewCheque({ ...newCheque, description: e.target.value })} placeholder="Açıklama giriniz..." className="!py-2.5 !rounded-xl text-xs" />
                    </div>
                </div>
            </Dialog>

            {/* ========================================== */}
            {/* 2. FİRMA ÇEKİ ÖDEME YAP (BANKADAN DÜŞ) DİALOG */}
            {/* ========================================== */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-slate-800 dark:text-white">
                        <i className="pi pi-check-circle text-emerald-600 text-lg" />
                        <span className="font-bold text-base">Firma Çeki Ödemesini Gerçekleştir</span>
                    </div>
                }
                visible={payDialogVisible}
                style={{ width: '460px' }}
                modal
                onHide={() => setPayDialogVisible(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="Vazgeç" icon="pi pi-times" onClick={() => setPayDialogVisible(false)} className="p-button-text !rounded-xl" disabled={actionLoading} />
                        <Button
                            label="Ödemeyi Onayla (Bankadan Düş)"
                            icon="pi pi-check"
                            onClick={handleConfirmPayment}
                            loading={actionLoading}
                            className="!bg-emerald-600 hover:!bg-emerald-700 border-none !rounded-xl font-bold text-xs py-2.5 text-white"
                        />
                    </div>
                }
            >
                {selectedChequeToPay && (
                    <div className="flex flex-col gap-4 mt-2">
                        <div className="bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-900/50 rounded-2xl p-3.5 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Çek Numarası:</span>
                                <span className="font-black text-slate-800 dark:text-white">{selectedChequeToPay.chequeNumber}</span>
                            </div>
                            {selectedChequeToPay.supplierName && (
                                <div className="flex justify-between items-center text-xs">
                                    <span className="text-slate-500 font-medium">Verilen Tedarikçi:</span>
                                    <span className="font-bold text-slate-800 dark:text-white">{selectedChequeToPay.supplierName}</span>
                                </div>
                            )}
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Vade Tarihi:</span>
                                <span className="font-bold text-slate-800 dark:text-white">{new Date(selectedChequeToPay.dueDate).toLocaleDateString('tr-TR')}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200/60 dark:border-emerald-900/40">
                                <span className="font-black text-emerald-800 dark:text-emerald-300">Ödenecek Tutar:</span>
                                <span className="text-base font-black text-emerald-700 dark:text-emerald-400">
                                    ₺{(Number(selectedChequeToPay.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                Çıkış Yapılacak Banka Hesabı <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={payForm.financialAccountId}
                                options={accountOptions}
                                onChange={(e) => setPayForm({ ...payForm, financialAccountId: e.value })}
                                placeholder="Banka Hesabı Seçin"
                                className="w-full rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                            <span className="text-[10px] text-slate-400">
                                Onaylandığında bu banka hesabından çek tutarı kadar çıkış (OUTBOUND) işlemi kaydedilir.
                            </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">İşlem Tarihi</label>
                            <Calendar
                                value={payForm.transactionDate}
                                onChange={(e) => setPayForm({ ...payForm, transactionDate: e.value })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                className="w-full"
                                inputClassName="!py-2.5 rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Açıklama</label>
                            <InputText
                                value={payForm.description}
                                onChange={(e) => setPayForm({ ...payForm, description: e.target.value })}
                                className="!py-2.5 !rounded-xl text-xs"
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ========================================== */}
            {/* 3. MÜŞTERİ ÇEKİ TAHSİL ET DİALOG */}
            {/* ========================================== */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-slate-800 dark:text-white">
                        <i className="pi pi-money-bill text-emerald-600 text-lg" />
                        <span className="font-bold text-base">Müşteri Çekini Tahsil Et</span>
                    </div>
                }
                visible={collectDialogVisible}
                style={{ width: '460px' }}
                modal
                onHide={() => setCollectDialogVisible(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="Vazgeç" icon="pi pi-times" onClick={() => setCollectDialogVisible(false)} className="p-button-text !rounded-xl" disabled={actionLoading} />
                        <Button
                            label="Tahsilatı Onayla (Hesaba Gir)"
                            icon="pi pi-check"
                            onClick={handleConfirmCollection}
                            loading={actionLoading}
                            className="!bg-emerald-600 hover:!bg-emerald-700 border-none !rounded-xl font-bold text-xs py-2.5 text-white"
                        />
                    </div>
                }
            >
                {selectedChequeToCollect && (
                    <div className="flex flex-col gap-4 mt-2">
                        <div className="bg-emerald-50/70 dark:bg-emerald-950/30 border border-emerald-200/80 dark:border-emerald-900/50 rounded-2xl p-3.5 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Müşteri:</span>
                                <span className="font-black text-slate-800 dark:text-white">{selectedChequeToCollect.customerName || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Çek No / Banka:</span>
                                <span className="font-bold text-slate-800 dark:text-white">{selectedChequeToCollect.chequeNumber} ({selectedChequeToCollect.bankName})</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Vade Tarihi:</span>
                                <span className="font-bold text-slate-800 dark:text-white">{new Date(selectedChequeToCollect.dueDate).toLocaleDateString('tr-TR')}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-emerald-200/60 dark:border-emerald-900/40">
                                <span className="font-black text-emerald-800 dark:text-emerald-300">Tahsil Edilecek Tutar:</span>
                                <span className="text-base font-black text-emerald-700 dark:text-emerald-400">
                                    ₺{(Number(selectedChequeToCollect.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                Tahsilatın Yatırılacağı Kasa / Banka Hesabı <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={collectForm.financialAccountId}
                                options={accountOptions}
                                onChange={(e) => setCollectForm({ ...collectForm, financialAccountId: e.value })}
                                placeholder="Hesap / Kasa Seçin"
                                className="w-full rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                            <span className="text-[10px] text-slate-400">
                                Onaylandığında bu finans hesabının bakiyesine çek tutarı kadar giriş (INBOUND) işlemi kaydedilir.
                            </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Tahsilat Tarihi</label>
                            <Calendar
                                value={collectForm.transactionDate}
                                onChange={(e) => setCollectForm({ ...collectForm, transactionDate: e.value })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                className="w-full"
                                inputClassName="!py-2.5 rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Açıklama</label>
                            <InputText
                                value={collectForm.description}
                                onChange={(e) => setCollectForm({ ...collectForm, description: e.target.value })}
                                className="!py-2.5 !rounded-xl text-xs"
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ========================================== */}
            {/* 4. MÜŞTERİ ÇEKİ TEDARİKÇİYE CİRO ET DİALOG */}
            {/* ========================================== */}
            <Dialog
                header={
                    <div className="flex items-center gap-2 text-slate-800 dark:text-white">
                        <i className="pi pi-sync text-blue-600 text-lg" />
                        <span className="font-bold text-base">Müşteri Çekini Tedarikçiye Ciro Et</span>
                    </div>
                }
                visible={endorseDialogVisible}
                style={{ width: '460px' }}
                modal
                onHide={() => setEndorseDialogVisible(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button label="Vazgeç" icon="pi pi-times" onClick={() => setEndorseDialogVisible(false)} className="p-button-text !rounded-xl" disabled={actionLoading} />
                        <Button
                            label="Ciro İşlemini Onayla"
                            icon="pi pi-check"
                            onClick={handleConfirmEndorsement}
                            loading={actionLoading}
                            className="!bg-blue-600 hover:!bg-blue-700 border-none !rounded-xl font-bold text-xs py-2.5 text-white"
                        />
                    </div>
                }
            >
                {selectedChequeToEndorse && (
                    <div className="flex flex-col gap-4 mt-2">
                        <div className="bg-blue-50/70 dark:bg-blue-950/30 border border-blue-200/80 dark:border-blue-900/50 rounded-2xl p-3.5 space-y-2">
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Müşteri:</span>
                                <span className="font-black text-slate-800 dark:text-white">{selectedChequeToEndorse.customerName || '-'}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Çek No / Banka:</span>
                                <span className="font-bold text-slate-800 dark:text-white">{selectedChequeToEndorse.chequeNumber} ({selectedChequeToEndorse.bankName})</span>
                            </div>
                            <div className="flex justify-between items-center text-xs">
                                <span className="text-slate-500 font-medium">Vade Tarihi:</span>
                                <span className="font-bold text-slate-800 dark:text-white">{new Date(selectedChequeToEndorse.dueDate).toLocaleDateString('tr-TR')}</span>
                            </div>
                            <div className="flex justify-between items-center text-xs pt-1 border-t border-blue-200/60 dark:border-blue-900/40">
                                <span className="font-black text-blue-800 dark:text-blue-300">Ciro Edilecek Tutar:</span>
                                <span className="text-base font-black text-blue-700 dark:text-blue-400">
                                    ₺{(Number(selectedChequeToEndorse.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                </span>
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                Ciro Edilecek Tedarikçi <span className="text-red-500">*</span>
                            </label>
                            <Dropdown
                                value={endorseForm.supplierId}
                                options={supplierOptions}
                                onChange={(e) => setEndorseForm({ ...endorseForm, supplierId: e.value })}
                                placeholder="Tedarikçi Seçin"
                                filter
                                className="w-full rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                            <span className="text-[10px] text-slate-400">
                                Onaylandığında bu tedarikçinin carisine çek tutarı kadar ödeme (borç düşüşü) işlenir.
                            </span>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Ciro Tarihi</label>
                            <Calendar
                                value={endorseForm.endorsementDate}
                                onChange={(e) => setEndorseForm({ ...endorseForm, endorsementDate: e.value })}
                                dateFormat="dd.mm.yy"
                                showIcon
                                className="w-full"
                                inputClassName="!py-2.5 rounded-xl border-slate-200 font-bold text-xs"
                                appendTo={mounted ? document.body : "self"}
                            />
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Açıklama</label>
                            <InputText
                                value={endorseForm.description}
                                onChange={(e) => setEndorseForm({ ...endorseForm, description: e.target.value })}
                                className="!py-2.5 !rounded-xl text-xs"
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ========================================== */}
            {/* 5. ÇEK BİLGİLERİNİ DÜZENLE DİALOG */}
            {/* ========================================== */}
            <Dialog
                header={
                    <div className="flex items-center gap-3 text-slate-800 dark:text-white" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                            className="rounded-xl bg-amber-100 dark:bg-amber-900/60 flex items-center justify-center text-amber-600 dark:text-amber-400"
                            style={{ width: '36px', height: '36px', minWidth: '36px', maxWidth: '36px', flexShrink: 0 }}
                        >
                            <i className="pi pi-pencil text-sm" />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-black text-base leading-tight">
                                {selectedChequeToEdit?.type === 2 ? 'Firma Çeki Bilgilerini Düzenle' : 'Müşteri Çeki Bilgilerini Düzenle'}
                            </span>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-normal m-0 mt-0.5">
                                Portföyde bekleyen çekin hatalı girilen alanlarını güncelleyebilirsiniz
                            </p>
                        </div>
                    </div>
                }
                visible={editDialogVisible}
                style={{ width: '520px' }}
                modal
                onHide={() => setEditDialogVisible(false)}
                footer={
                    <div className="flex justify-end gap-2">
                        <Button
                            label="Vazgeç"
                            icon="pi pi-times"
                            onClick={() => setEditDialogVisible(false)}
                            className="p-button-text !rounded-xl"
                            disabled={actionLoading}
                        />
                        <Button
                            label="Değişiklikleri Kaydet"
                            icon="pi pi-check"
                            onClick={handleConfirmEdit}
                            loading={actionLoading}
                            className="!bg-amber-600 hover:!bg-amber-700 border-none !rounded-xl font-bold text-xs py-2.5 px-4 text-white shadow-md shadow-amber-600/20"
                        />
                    </div>
                }
            >
                {selectedChequeToEdit && (
                    <div className="flex flex-col gap-4 mt-2">
                        {/* Müşteri Seçimi (Sadece Müşteri Çeki ise) */}
                        {selectedChequeToEdit.type === 1 && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Alınan Müşteri (Cari Hesap)
                                </label>
                                <Dropdown
                                    value={editForm.customerId}
                                    options={customerOptions}
                                    onChange={(e) => setEditForm({ ...editForm, customerId: e.value })}
                                    placeholder="Müşteri Seçin"
                                    filter
                                    showClear
                                    className="w-full rounded-xl border-slate-200 font-bold text-xs"
                                    appendTo={mounted ? document.body : "self"}
                                />
                            </div>
                        )}

                        {/* Bağlı Banka Hesabı (Sadece Firma Çeki ise) */}
                        {selectedChequeToEdit.type === 2 && (
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Bağlı Banka Hesabı (Sistem Kayıtlı)
                                </label>
                                <Dropdown
                                    value={editForm.financialAccountId}
                                    options={accountOptions}
                                    onChange={(e) => {
                                        const selectedAcc = accountOptions.find(a => a.value === e.value)?.account;
                                        setEditForm({
                                            ...editForm,
                                            financialAccountId: e.value,
                                            bankName: selectedAcc?.name ? selectedAcc.name : editForm.bankName
                                        });
                                    }}
                                    placeholder="Banka Hesabı Seçin (Opsiyonel)"
                                    showClear
                                    filter
                                    className="w-full rounded-xl border-slate-200 font-bold text-xs"
                                    appendTo={mounted ? document.body : "self"}
                                />
                                <span className="text-[10px] text-slate-400">
                                    Sistemdeki banka hesabınızı bağlarsanız, ödendiğinde bakiye otomatik bu hesaptan düşülür.
                                </span>
                            </div>
                        )}

                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Çek Numarası <span className="text-red-500">*</span>
                                </label>
                                <InputText
                                    value={editForm.chequeNumber}
                                    onChange={(e) => setEditForm({ ...editForm, chequeNumber: e.target.value })}
                                    placeholder="Örn: 001245"
                                    className="!py-2.5 !rounded-xl text-xs font-bold"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Vade Tarihi <span className="text-red-500">*</span>
                                </label>
                                <Calendar
                                    value={editForm.dueDate}
                                    onChange={(e) => setEditForm({ ...editForm, dueDate: e.value })}
                                    dateFormat="dd.mm.yy"
                                    showIcon
                                    className="!rounded-xl"
                                    inputClassName="!py-2.5 text-xs font-bold"
                                    appendTo={mounted ? document.body : "self"}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Banka Adı</label>
                                <InputText
                                    value={editForm.bankName}
                                    onChange={(e) => setEditForm({ ...editForm, bankName: e.target.value })}
                                    placeholder="Örn: Garanti BBVA"
                                    className="!py-2.5 !rounded-xl text-xs"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Banka Şubesi</label>
                                <InputText
                                    value={editForm.bankBranch}
                                    onChange={(e) => setEditForm({ ...editForm, bankBranch: e.target.value })}
                                    placeholder="Örn: Kadıköy Şb."
                                    className="!py-2.5 !rounded-xl text-xs"
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-3">
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Keşideci (Borçlu)
                                </label>
                                <InputText
                                    value={editForm.drawer}
                                    onChange={(e) => setEditForm({ ...editForm, drawer: e.target.value })}
                                    placeholder="Keşideci Adı..."
                                    className="!py-2.5 !rounded-xl text-xs"
                                />
                            </div>
                            <div className="flex flex-col gap-1.5">
                                <label className="text-xs font-bold text-slate-700 dark:text-slate-300">
                                    Çek Tutarı (TL) <span className="text-red-500">*</span>
                                </label>
                                <InputNumber
                                    value={editForm.amount}
                                    onValueChange={(e) => setEditForm({ ...editForm, amount: e.value })}
                                    mode="currency"
                                    currency="TRY"
                                    locale="tr-TR"
                                    className="!rounded-xl"
                                    inputClassName="!py-2.5 font-bold text-sm"
                                />
                            </div>
                        </div>

                        <div className="flex flex-col gap-1.5">
                            <label className="text-xs font-bold text-slate-700 dark:text-slate-300">Açıklama</label>
                            <InputText
                                value={editForm.description}
                                onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                                placeholder="Açıklama veya not giriniz..."
                                className="!py-2.5 !rounded-xl text-xs"
                            />
                        </div>
                    </div>
                )}
            </Dialog>

            {/* ========================================== */}
            {/* 6. BANKA HESABI EŞLEŞTİRME POPUP */}
            {/* ========================================== */}
            <Dialog
                header={
                    <div className="flex items-center gap-3 text-slate-800 dark:text-white" style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                        <div
                            className="rounded-xl bg-amber-500 text-white flex items-center justify-center shadow-md shadow-amber-500/20"
                            style={{ width: '36px', height: '36px', minWidth: '36px', maxWidth: '36px', flexShrink: 0 }}
                        >
                            <i className="pi pi-link text-base" />
                        </div>
                        <div className="flex flex-col">
                            <span className="font-black text-base leading-tight">
                                Banka Hesabı Bağlantısı Eksik Çekler
                            </span>
                            <p className="text-[11px] text-slate-500 dark:text-slate-400 font-normal m-0 mt-0.5">
                                Sistemdeki firma çeklerinizin banka hesaplarını eşleştirerek otomatik bakiye takibini aktifleştirin
                            </p>
                        </div>
                    </div>
                }
                visible={unlinkedModalVisible}
                style={{ width: '780px', maxWidth: '95vw' }}
                modal
                onHide={() => setUnlinkedModalVisible(false)}
                footer={
                    <div className="flex justify-between items-center w-full">
                        <span className="text-[11px] text-slate-400 font-medium">
                            {unlinkedChequesList.filter((x) => x.targetAccountId).length} / {unlinkedChequesList.length} çek için hesap seçildi
                        </span>
                        <div className="flex gap-2">
                            <Button
                                label="Daha Sonra Hatırlat"
                                icon="pi pi-times"
                                onClick={() => setUnlinkedModalVisible(false)}
                                className="p-button-text !rounded-xl !text-xs !font-bold"
                                disabled={savingUnlinked}
                            />
                            <Button
                                label="Seçilenleri Kaydet & Eşleştir"
                                icon="pi pi-check"
                                onClick={handleSaveUnlinkedMappings}
                                loading={savingUnlinked}
                                className="!bg-emerald-600 hover:!bg-emerald-700 border-none !rounded-xl font-bold text-xs py-2.5 px-4 text-white shadow-md shadow-emerald-600/20"
                            />
                        </div>
                    </div>
                }
            >
                <div className="flex flex-col gap-3 mt-2">
                    <div className="p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-900/50 rounded-2xl flex items-center gap-2.5 text-xs text-blue-900 dark:text-blue-200">
                        <i className="pi pi-info-circle text-base text-blue-600 shrink-0" />
                        <span>
                            Aşağıdaki firma çekleri daha önce banka hesabı seçilmeden kaydedilmiş. Her çek için ilgili banka hesabınızı seçip tek seferde kaydedebilirsiniz.
                        </span>
                    </div>

                    <div className="max-h-[380px] overflow-y-auto rounded-2xl border border-slate-200 dark:border-slate-800">
                        <table className="w-full text-left text-xs border-collapse">
                            <thead className="bg-slate-100 dark:bg-slate-800/80 sticky top-0 z-10">
                                <tr className="border-b border-slate-200 dark:border-slate-700 text-slate-600 dark:text-slate-300 font-black">
                                    <th className="p-3">Çek No</th>
                                    <th className="p-3">Vade Tarihi</th>
                                    <th className="p-3">Tutar</th>
                                    <th className="p-3">Kayıtlı Banka Metni</th>
                                    <th className="p-3 min-w-[220px]">Bağlanacak Banka Hesabı</th>
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-slate-100 dark:divide-slate-800 bg-white dark:bg-slate-900">
                                {unlinkedChequesList.map((item, idx) => (
                                    <tr key={item.id || idx} className="hover:bg-slate-50/80 dark:hover:bg-slate-800/50 transition-colors">
                                        <td className="p-3 font-bold text-slate-800 dark:text-white">
                                            {item.chequeNumber || "-"}
                                        </td>
                                        <td className="p-3 font-medium text-slate-600 dark:text-slate-300">
                                            {item.dueDate ? new Date(item.dueDate).toLocaleDateString('tr-TR') : "-"}
                                        </td>
                                        <td className="p-3 font-black text-rose-600 dark:text-rose-400">
                                            ₺{(Number(item.amount) || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                        </td>
                                        <td className="p-3 text-slate-500 font-medium">
                                            {item.bankName || "-"} {item.bankBranch ? `(${item.bankBranch})` : ""}
                                        </td>
                                        <td className="p-3">
                                            <Dropdown
                                                value={item.targetAccountId}
                                                options={accountOptions}
                                                onChange={(e) => {
                                                    const updated = [...unlinkedChequesList];
                                                    updated[idx].targetAccountId = e.value;
                                                    setUnlinkedChequesList(updated);
                                                }}
                                                placeholder="Banka Hesabı Seç..."
                                                showClear
                                                filter
                                                className="w-full rounded-xl border-slate-200 text-xs font-bold"
                                                appendTo={mounted ? document.body : "self"}
                                            />
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            </Dialog>
        </div>
    );
}
